import EligibilityToggleValue from '@components/primitives/ToggleButton/EligibilityToggleGroup/EligibilityToggleValue'

export type DetailedMedicationData = {
  isMedicationHelping?: string
  name?: string
  stableDose?: string
  willingToStop?: string
}[]

/**
 * Create object containing medication name and details about if it is helping, stable dose, or willing to stop
 * For "Unsure" answer, or if the question was skipped, the sub-class value is undefined. Otherwise, it is a string boolean
 *
 * @param areCurrentMedicationsHelping Patient answer to question about if current medications are helping
 * @param areCurrentMedicationsStableDose Patient answer to question about if current medications are stable dose
 * @param areCurrentMedicationsWillingToStop Patient answer to question about if current medications are willing to stop
 * @param drugNamesPatientSelected Array of drug names selected by patient
 *
 * @return array of medications with details provided by patient
 */
export async function createDetailedMedicationData({
  areCurrentMedicationsHelping,
  areCurrentMedicationsStableDose,
  areCurrentMedicationsWillingToStop,
  drugNamesPatientSelected,
}: {
  areCurrentMedicationsHelping?: string
  areCurrentMedicationsStableDose?: string
  areCurrentMedicationsWillingToStop?: string
  drugNamesPatientSelected: string[]
}): Promise<DetailedMedicationData> {
  const medications = []
  for (const drug of drugNamesPatientSelected) {
    // In the eligibility concept table, the name is stored as string - so we need to convert the yes/no/unsure or skipped question to a string boolean. The column is polymorphic.
    const isMedicationHelping = getYesNoOrUndefinedSubClassAnswer(
      areCurrentMedicationsHelping,
    )
    const stableDose = getYesNoOrUndefinedSubClassAnswer(
      areCurrentMedicationsStableDose,
    )
    const willingToStop = getYesNoOrUndefinedSubClassAnswer(
      areCurrentMedicationsWillingToStop,
    )

    medications.push({
      name: drug,
      ...(isMedicationHelping && { isMedicationHelping }),
      ...(stableDose && { stableDose }),
      ...(willingToStop && { willingToStop }),
    })
  }
  return medications
}

/**
 * Convert yes/no string to true/false string
 * In all other cases, return undefined
 *
 * @param value String to convert to boolean
 *
 * @return boolean or undefined
 */
export function getYesNoOrUndefinedSubClassAnswer(value?: string) {
  if (value?.toLowerCase() === EligibilityToggleValue.YES.toLowerCase()) {
    return String(true)
  } else if (value?.toLowerCase() === EligibilityToggleValue.NO.toLowerCase()) {
    return String(false)
  }
  return
}
